// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable from "@guestsupply/App/Components/Header/HeaderClickable";
import xngGaToolBarNavigationEvent from "@guestsupply/App/Handlers/GoogleAnalytics/ToolBarNavigation/ToolBarNavigationEvent";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { customXngageWidget, GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import Guide from "@guestsupply/XDL/Icons/Guide";
import { xScreen } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showAsLink?: boolean;
}

export interface OrderGuideStyles {
    link?: LinkPresentationProps;
}

// xng-change: add google event
const mapStateToProps = (state: CustomAppState) => ({});

// xng-change: add google event
const mapDispatchToProps = {
    xngGaToolBarNavigationEvent,
};

export const orderGuideStyles: OrderGuideStyles = {};

const styles = orderGuideStyles;

// xng-change: add google event
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OrderGuide: React.FC<Props> = ({
    showAsLink = false,
    // xng-change: add google event
    xngGaToolBarNavigationEvent,
}) => {
    const { wishListId, listDetailsPageLink, listsPageLink } = useAppSelector(state => {
        const session = getSession(state);
        const wishListId = session.properties.orderFormId;
        const listDetailsPageLink = getPageLinkByPageType(state, "MyListsDetailsPage")?.url;
        const listsPageLink = getPageLinkByPageType(state, "MyListsPage")?.url;
        return {
            wishListId,
            listDetailsPageLink,
            listsPageLink,
        };
    });

    const history = useHistory();

    const navigateToOrderGuidePage = () => {
        // xng-change: add google event
        xngGaToolBarNavigationEvent({
            clickText: translate("My Order Guide"),
        });

        const orderGuideUrl = wishListId ? `${listDetailsPageLink}?id=${wishListId}` : `${listsPageLink}`;

        history.push(orderGuideUrl);
    };

    return (
        <>
            {showAsLink ? (
                <Link
                    {...styles.link}
                    onClick={navigateToOrderGuidePage}
                    data-test-selector="OrderGuide"
                    className="main-navigation-row-text"
                >
                    {translate("My Order Guide")}
                </Link>
            ) : (
                <StyledWrapper data-test-selector="OrderGuide">
                    <HeaderClickable
                        onClick={navigateToOrderGuidePage}
                        iconProps={{ src: Guide }}
                        title={translate("My Order Guide")}
                        extendedStyles={{
                            icon: {
                                css: css`
                                    ${xScreen("lte", "sm")} {
                                        display: none;
                                    }
                                `,
                            },
                        }}
                    />
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderGuide),
    definition: {
        displayName: "Order Guide",
        group: customXngageWidget as any,
    },
};

export default widgetModule;
